// pages/master/customer/MasterCustomerPage.jsx
'use client';

import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../components/DataTable';
import ActionMenu from '../../../components/ActionMenu';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';

import { useCustomerList, deleteCustomer } from '../../../stores/customerStore';
import { useCompanyList } from '../../../stores/companyStore';
import MasterCustomerAddEdit from './MasterCustomerAddEdit';
import { useLanguage } from '../../../utils/i18n';

// TAMBAHKAN IMPORT fleetApi UNTUK HIT API LOKAL
import { fleetApi } from '../../../api/axiosClient';

function MasterCustomerPageContent() {
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const notify = useNotifier();
  const { t } = useLanguage();

  // --- 1. SETUP COMPANY FILTER & TENANT LOGIC ---
  const { companyOptions } = useCompanyList();
  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;

      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }

      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;

      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];

      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('Auto select company from user failed', e);
    }
  }, [companyOptionsNormalized]);

  const filterCompanyIds = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return '';
    const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
    if (hasAll) return '';
    return selectedCompanies.map((s) => String(s.value)).join(',');
  }, [selectedCompanies]);

  // --- 2. SEARCH & DATA FETCHING ---
  const [searchField, setSearchField] = useState('name');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  // STATE LOKAL UNTUK PENCARIAN 'ALL'
  const [localCustomers, setLocalCustomers] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'name', label: t('customer.name', 'Customer Name') },
      { value: 'code', label: t('customer.code', 'Customer Code') },
      { value: 'phone', label: t('masterCustomer.phone', 'Phone') },
      {
        value: 'business_type',
        label: t('masterCustomer.customerType', 'Customer Type'),
        tableName: 'm_business_type',
        fieldName: 'name',
        joinKey: 'business_type'
      }
    ],
    [t]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);

    return () => clearTimeout(t);
  }, [searchRaw, searchField]);

  // Cegah hook menembak parameter 'all' ke API bawaannya
  const hookSearchField = searchField === 'all' ? '' : searchField;
  const hookSearchValue = searchField === 'all' ? '' : searchValue;

  const {
    data: customersFromHook,
    totalRecord: hookTotal,
    isLoading: hookLoading
  } = useCustomerList(paginationModel.page, paginationModel.pageSize, hookSearchField, hookSearchValue, filterCompanyIds);

  // Penentuan sumber data: Pakai state lokal jika 'all', selain itu pakai hook
  const rows = searchField === 'all' ? localCustomers : customersFromHook || [];
  const isLoading = searchField === 'all' ? localLoading : hookLoading;
  const totalRecords = searchField === 'all' ? localTotal : hookTotal || 0;

  // --- 3. EFEK KHUSUS PENCARIAN 'ALL' ---
  useEffect(() => {
    if (searchField !== 'all') return;
    const controller = new AbortController();

    const fetchAllSearch = async () => {
      setLocalLoading(true);
      try {
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;

        // 1. Rakit array 'columns' untuk menyimpan filter company_id
        const columns = [];
        if (selectedCompanies && selectedCompanies.length > 0) {
          const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
          if (!hasAll) {
            const values = selectedCompanies.map((s) => String(s.value));
            if (values.length === 1) {
              columns.push({ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] });
            } else {
              columns.push({ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') });
            }
          }
        }

        // Default minimal object untuk menghindari error backend jika array columns kosong
        if (columns.length === 0) {
          columns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });
        }

        let filters = [];
        if (searchValue) {
          filters = [
            { name: 'name', value: searchValue },
            { name: 'code', value: searchValue },
            { name: 'phone', value: searchValue },
            { name: 'name', table_name: 'm_company', value: searchValue },
            { name: 'name', table_name: 'm_business_type', value: searchValue }
          ];
        }

        const payload = {
          paging: { start, length },
          orders: { columns: ['name'], ascending: true },
          joins: [
            { name: 'company', column_results: ['name'] },
            { name: 'business_type', column_results: ['name'] }
          ],
          columns: columns,
          filters: filters
        };

        const resp = await fleetApi.post('/customer/search', payload, { signal: controller.signal });
        const data = resp?.data ?? null;

        const fetchedRows = Array.isArray(data) ? data : (data?.data ?? []);
        const total = Array.isArray(data) ? fetchedRows.length : (data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? 0);

        setLocalCustomers(fetchedRows);
        setLocalTotal(total);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('customer search all error', err);
      } finally {
        if (!controller.signal.aborted) setLocalLoading(false);
      }
    };

    fetchAllSearch();
    return () => controller.abort();
  }, [searchField, searchValue, paginationModel, selectedCompanies]);

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecords > 0 ? totalRecords : 9999;
      const columns = [];
      if (selectedCompanies && selectedCompanies.length > 0) {
        const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
        if (!hasAll) {
          const values = selectedCompanies.map((s) => String(s.value));
          if (values.length === 1) {
            columns.push({ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] });
          } else {
            columns.push({ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') });
          }
        }
      }

      if (columns.length === 0) {
        columns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });
      }

      let filters = [];
      if (searchField === 'all' && searchValue) {
        filters = [
          { name: 'name', value: searchValue },
          { name: 'code', value: searchValue },
          { name: 'phone', value: searchValue },
          { name: 'name', table_name: 'm_company', value: searchValue },
          { name: 'name', table_name: 'm_business_type', value: searchValue }
        ];
      } else if (searchValue) {
        const opt = searchFieldOptions.find((o) => o.value === searchField);
        if (opt) {
          const searchCol = {
            name: opt.fieldName || searchField,
            logic_operator: 'like',
            operator: 'AND',
            value: searchValue
          };
          if (opt.tableName) searchCol.table_name = opt.tableName;
          columns.push(searchCol);
        }
      }

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['name'], ascending: true },
        joins: [
          { name: 'company', column_results: ['name'] },
          { name: 'business_type', column_results: ['name'] },
          { name: 'country', column_results: ['name'] }
        ],
        columns: columns,
        filters: filters
      };

      const resp = await fleetApi.post('/customer/search', payload);
      const data = resp?.data ?? null;
      return Array.isArray(data) ? data : (data?.data ?? []);
    } catch (err) {
      console.error('customer export all error', err);
      return [];
    }
  };

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  };

  const handleCompanyChange = (options = []) => {
    const sanitized = (options || []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
    setSelectedCompanies(sanitized);
    try {
      if (typeof window !== 'undefined') {
        if (sanitized.length === 1) localStorage.setItem('companyId', String(sanitized[0].value));
        else localStorage.removeItem('companyId');
      }
    } catch {
      console.warn('Gagal menyimpan companyId ke localStorage');
    }
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  // --- 5. DELETE FLOW ---
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (id, itemName) => {
    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteCustomer(confirmTargetId);
      notify({ message: t('customer.notifSucces', 'Customer successfully deleted'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error(err);
      notify({ message: t('customer.notifFailed', 'Failed to delete Customer'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  // --- 6. RENDER COLUMNS ---
  const getCompanyLabel = (row) => {
    if (!row) return '-';
    if (row?.join?.company_name) return row.join.company_name;
    const companyId = row?.company ?? row?.company_id ?? null;
    if (companyId && companyOptionsNormalized) {
      const found = companyOptionsNormalized.find((c) => String(c.value) === String(companyId));
      if (found) return found.label;
    }
    return '-';
  };

  const getTypeLabel = (row) => {
    if (!row) return '-';
    if (row?.join?.business_type_name) return row.join.business_type_name;
    return '-';
  };

  const columns = [
    {
      field: 'company',
      headerName: t('general.company', 'Company'),
      flex: 1.5,
      renderCell: (params) => getCompanyLabel(params.row),
      valueGetter: (val, row) => getCompanyLabel(row)
    },
    { field: 'name', headerName: t('customer.name', 'Customer Name'), flex: 2 },
    { field: 'code', headerName: t('customer.code', 'Customer Code'), flex: 1 },
    {
      field: 'type',
      headerName: t('customer.type', 'Customer Type'),
      flex: 1.5,
      renderCell: (params) => getTypeLabel(params.row),
      valueGetter: (val, row) => getTypeLabel(row)
    },
    {
      field: 'country',
      headerName: t('masterCustomer.country', 'Country'),
      flex: 1,
      hide: true,
      valueGetter: (params, row) => row?.join?.country_name || '-'
    },
    { field: 'address', headerName: t('masterCustomer.address', 'Address'), flex: 1, hide: true },
    { field: 'pic_name', headerName: t('masterCustomer.picName', 'PIC Name'), flex: 1, hide: true },
    {
      field: 'phone1',
      headerName: t('customer.phone', 'Customer Phone'),
      flex: 1.5,
      valueGetter: (val, row) => row.phone1 || row.phone || ''
    },
    {
      field: 'fax1',
      headerName: t('masterCustomer.fax', 'Fax'),
      flex: 1,
      hide: true,
      valueGetter: (val, row) => row.fax1 || row.fax || ''
    },
    { field: 'website', headerName: t('masterCustomer.website', 'Website'), flex: 1, hide: true },

    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'npwp_no', headerName: t('masterCustomer.npwpNumber', 'NPWP Number'), flex: 0.5, hide: true },
          { field: 'npwp_address', headerName: t('masterCustomer.npwpAddress', 'NPWP Address'), flex: 0.5, hide: true },
          { field: 'created_by', headerName: t('masterCustomer.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('masterCustomer.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('masterCustomer.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('masterCustomer.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      width: 120,
      headerAlign: 'center',
      renderCell: (params) => (
        <ActionMenu row={params.row} onEdit={handleEditClick} onDelete={(id) => handleDelete(id, params.row.name)} mode="inline" />
      )
    }
  ];

  return (
    <div>
      <DataTable
        title={t('masterCustomer.title', 'Master Customer')}
        onFetchAllForExport={handleFetchAllForExport}
        rows={rows || []}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => {
          setSearchField(field);
          setSearchRaw(value ?? '');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        searchLabel={t('customer.search', 'Search Customer')}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
      />

      {openDialog && (
        <MasterCustomerAddEdit
          open={openDialog}
          id={editingId}
          companyOptions={companyOptionsNormalized}
          onClose={handleDialogClose}
          onSuccess={() => setPaginationModel((p) => ({ ...p }))}
        />
      )}

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
      />
    </div>
  );
}

export default function MasterCustomerPage() {
  return <MasterCustomerPageContent />;
}
